import useAuth from "./hooks/useAuth";
import { useEffect, useState } from "react";
import CategoryDropdownRoles from "./CategoryDropdownRoles";

const UserSettings = () => {
    const {auth, setAuth} = useAuth();
    const [requestRole, setRequestRole] = useState("");
    const [newName, setNewName] = useState("");
    const [nameError, setNameError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const requestAccess = async () => {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        try {
            const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/request-access/${requestRole}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });
            if (response.status === 409) {
                localStorage.removeItem("token");
                setAuth({});
            }
        } catch (error) {
            console.error("Error requesting access:", error);
        } finally {
            setIsLoading(false);
        }
    } 
    
    const updateName = async () => {
        setNameError("");
        setIsLoading(true);
        const token = localStorage.getItem("token");
        
        try {
            const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/update-username/${newName}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }
            });
            
            if (response.ok) {
                const userResponse = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/me`, {
                    method: "GET",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`,
                    },
                });
                if (userResponse.ok) {
                    let userData = await userResponse.json();
                    userData["token"] = token;
                    setAuth(userData);
                    setNewName(""); // Clear input on success
                }
            } else {
                setNameError(`Username "${newName}" is already taken`);
            }
            
            if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
            }
        } catch (error) {
            console.error("Error updating username:", error);
            setNameError("Failed to update username");
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div className="w-full max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-blue-700">User Settings</h2>
                <p className="text-gray-600 mt-2">Manage your account preferences</p>
            </div>

            {/* User Info Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium text-blue-600 mb-1">Username</p>
                        <p className="text-lg font-semibold text-gray-700 truncate">{auth?.username}</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium text-blue-600 mb-1">Email Address</p>
                        <p className="text-lg font-semibold text-gray-700 truncate">{auth?.email}</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium text-blue-600 mb-1">Role</p>
                        <p className="text-lg font-semibold text-gray-700">{auth?.role}</p>
                    </div>
                </div>
            </div>

            {/* Settings Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Change Username */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Change Username</h3>
                    <div className="space-y-4">
                        <div>
                            <input 
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                placeholder="Enter new username"
                                type="text" 
                                onChange={(e) => setNewName(e.target.value)} 
                                value={newName} 
                            />
                        </div>
                        {nameError && (
                            <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">
                                {nameError}
                            </div>
                        )}
                        <button 
                            className={`w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                isLoading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            onClick={updateName}
                            disabled={!newName.trim() || isLoading}
                        >
                            {isLoading ? 'Updating...' : 'Update Username'}
                        </button>
                    </div>
                </div>

                {/* Change Role */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Request Role Change</h3>
                    <div className="space-y-4">
                        <div>
                            <CategoryDropdownRoles 
                                selectHandler={e => setRequestRole(e.target.value)} 
                                elValue={auth?.role} 
                            />
                        </div>
                        <button 
                            className={`w-full py-3 px-4 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                isLoading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            onClick={requestAccess}
                            disabled={!requestRole || isLoading}
                        >
                            {isLoading ? 'Requesting...' : 'Request Role Update'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UserSettings;